import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Text, TextField } from "@radix-ui/themes"
import { z } from "zod"

import FileUpload from "@/components/file-upload"
import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { projectListsPath, projectDashboardPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
})

type Props = z.infer<typeof PropsSchema>

export default function NewList(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project } = validatedProps

  const { data, setData, post, processing, errors } = useForm({
    name: "",
    description: "",
    csv_file: null as File | null,
  })

  const handleFileSelect = (file: File | null) => {
    setData("csv_file", file)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(projectListsPath(project.slug))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Custom Lists", href: projectListsPath(project.slug) },
        { title: "New list" },
      ]}
    >
      <Head title="New list" />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            Create new list
          </Heading>
          <Text color="gray" size="2">
            Create a custom list. You can optionally upload a CSV file containing customer email
            addresses.
          </Text>
        </Flex>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                List name
              </Text>
              <TextField.Root
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="Enter list name"
                disabled={processing}
                required
              />
              {errors.name && (
                <Text size="1" color="red">
                  {errors.name}
                </Text>
              )}
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                Description (optional)
              </Text>
              <TextField.Root
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
                placeholder="Enter list description"
                disabled={processing}
              />
              {errors.description && (
                <Text size="1" color="red">
                  {errors.description}
                </Text>
              )}
            </Flex>

            <FileUpload
              file={data.csv_file}
              onFileSelect={handleFileSelect}
              accept=".csv"
              label="CSV file with email addresses (optional)"
              description="Upload a CSV file with one column containing email addresses to pre-populate this list."
              error={errors.csv_file}
              disabled={processing}
            />

            <Flex gap="3" justify="end" mt="4">
              <LinkButton href={projectListsPath(project.slug)} variant="soft" color="gray">
                Cancel
              </LinkButton>

              <Button type="submit" disabled={!data.name.trim() || processing}>
                {processing ? "Creating..." : "Create list"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
